// @ts-strict-ignore
import type {CurrentProject} from 'common/report/model-resources/projects';
import {ProjectDetails} from 'common/report/stores/project-details';
import {getProjectId, isCurrentProjectInternal, isUrlDefinedExperimentalFeatureEnabled} from 'common/report/util';
import {isDevEnv, isTestEnv} from 'common/report/util/env';
import {getLocation} from 'common/util/url';
import {FEATURE_FLAG_VARIANTS} from 'apps/feature-flags/src/constants';

import {FeatureFlag, FeatureGate, ProjectFeatureFlag} from '../constants';

export const featureSamplingUtils = {
  determineFeatureEligibility,
};

/**
 * Check deterministically whether a user or group is included in a feature rollout/test based on a
 * unique user/group string identifier and the desired percentage to include.
 */
function determineFeatureEligibility(
  userOrGroupId: string,
  feature: string,
  percentAllowed: number /* must be in range [0, 100] */,
): boolean {
  if (!(percentAllowed >= 0 && percentAllowed <= 100)) {
    throw new Error(`@percentAllowed argument must be in range [0, 100] inclusive`);
  }

  if (typeof userOrGroupId === `undefined` || userOrGroupId === null || userOrGroupId === ``) {
    throw new Error(`@userOrGroupId argument cannot be null or undefined or empty`);
  }
  if (typeof feature === `undefined` || feature === null || feature === ``) {
    throw new Error(`@feature argument cannot be null or undefined or empty`);
  }

  switch (percentAllowed) {
    case 0:
      return false;
    case 100:
      return true;
    default:
      break;
  }

  const key = userOrGroupId + feature;

  // Bernstein's hash: http://www.cse.yorku.ca/~oz/hash.html#djb2
  // The djb2 hash was chosen because of the claim that "it has excellent distribution and speed on many different sets of keys and table sizes",
  // but we have not validated whether it is statistically reliable and accurate to use in sampling users/groups for rollouts and analysis.
  let hash = 5381;
  for (let i = 0; i < key.length; i++) {
    hash = (hash << 5) + hash + key.charCodeAt(i);
    hash = hash & hash;
  }
  const dart = (hash >>> 0) % 100;
  return dart < percentAllowed;
}

enum FeatureRolloutType {
  Project,
  Org,
}
export class FeatureRollout {
  private declare type: FeatureRolloutType;
  private declare percentage: number;
  public declare readonly featureflag: ProjectFeatureFlag;
  private explicitlyDisallowIds: string[] = [];

  public declare readonly name: FeatureRolloutName;

  constructor(params: {
    type: FeatureRolloutType;
    name: FeatureRolloutName;
    percentage: number;
    featureflag?: ProjectFeatureFlag;
    explicitlyDisallowIds?: string[];
  }) {
    this.type = params.type;
    this.name = params.name;
    this.percentage = params.percentage;
    this.featureflag = params.featureflag;
    this.explicitlyDisallowIds = params.explicitlyDisallowIds ?? [];
  }

  isEnabled(project: CurrentProject | ProjectDetails | null): boolean {
    if (this.type === FeatureRolloutType.Project) {
      const projectId = project?.id.toString() ?? getProjectId();
      if (this.explicitlyDisallowIds.includes(projectId)) {
        return false;
      }

      return (
        featureSamplingUtils.determineFeatureEligibility(projectId, this.name.toString(), this.percentage) ||
        project?.hasFeatureFlag(this.featureflag)
      );
    }
    const orgId = project?.organizationId.toString();
    if (this.explicitlyDisallowIds.includes(orgId)) {
      return false;
    }

    return orgId
      ? featureSamplingUtils.determineFeatureEligibility(orgId, this.name.toString(), this.percentage) ||
          project?.hasFeatureFlag(this.featureflag)
      : false;
  }
}

// Add any gradual feature rollouts here
export enum FeatureRolloutName {
  OnboardingChecklist = `onboarding-checklist`,
  Revenue = `revenue`,
  Home = `home`,
  WarehouseLaunchTemplates = `wh_launch_templates`,
  DisableVizOnNonCompliant = `disable_viz_non_comp`,
  CompanyProfile = `company_profile`,
  BoardBreakdowns = `board_breakdowns`,
  NullPropertyMonitoring = `null_property_monitoring`,
  OptimizedFunnelSelection = `optimized_funnels`,
  PropertyTypeMonitoring = `property_type_monitoring`,
  ScrollMaps = `scroll_maps`,
  ConcurrentReplays = `sr_concurrent_replays`,
  TextAnalysis = `text_analysis`,
  IssuesHub = `issues_hub`,
  IssuesHubSidePanel = `issues_hub_sidepanel`,
  LexiconCohortCountRefresh = `lexicon_cohort_count_refresh`,
  ViewNEverywhere = `view_n_everywhere`,
  MetricTreeAI = `metric_tree_ai`,
}

export const FeatureRollouts: {[key in FeatureRolloutName]: FeatureRollout} = {
  [FeatureRolloutName.OnboardingChecklist]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.OnboardingChecklist,
    percentage: 0,
  }),
  [FeatureRolloutName.Revenue]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.Revenue,
    featureflag: ProjectFeatureFlag.Revenue,
    percentage: 0,
  }),
  [FeatureRolloutName.Home]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.Home,
    percentage: 100,
    explicitlyDisallowIds: [`1866253`],
  }),
  [FeatureRolloutName.WarehouseLaunchTemplates]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.WarehouseLaunchTemplates,
    percentage: 0,
    featureflag: ProjectFeatureFlag.WarehouseLaunchTemplates,
  }),
  [FeatureRolloutName.DisableVizOnNonCompliant]: new FeatureRollout({
    type: FeatureRolloutType.Project,
    name: FeatureRolloutName.DisableVizOnNonCompliant,
    percentage: 0,
    featureflag: ProjectFeatureFlag.DisableVizOnNonCompliant,
  }),
  [FeatureRolloutName.CompanyProfile]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.CompanyProfile,
    percentage: 100,
    featureflag: ProjectFeatureFlag.CompanyProfile,
  }),
  [FeatureRolloutName.ScrollMaps]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.ScrollMaps,
    percentage: 0,
    featureflag: ProjectFeatureFlag.ScrollMaps,
  }),
  [FeatureRolloutName.ConcurrentReplays]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.ConcurrentReplays,
    percentage: 0,
    featureflag: ProjectFeatureFlag.ConcurrentReplays,
  }),
  [FeatureRolloutName.BoardBreakdowns]: new FeatureRollout({
    type: FeatureRolloutType.Project,
    name: FeatureRolloutName.BoardBreakdowns,
    percentage: 100,
    featureflag: ProjectFeatureFlag.BoardBreakdowns,
  }),
  [FeatureRolloutName.NullPropertyMonitoring]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.NullPropertyMonitoring,
    percentage: 50,
    featureflag: ProjectFeatureFlag.NullPropertyMonitoring,
  }),
  [FeatureRolloutName.PropertyTypeMonitoring]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.PropertyTypeMonitoring,
    percentage: 100,
    featureflag: ProjectFeatureFlag.PropertyTypeMonitoring,
  }),
  [FeatureRolloutName.OptimizedFunnelSelection]: new FeatureRollout({
    type: FeatureRolloutType.Project,
    name: FeatureRolloutName.OptimizedFunnelSelection,
    percentage: 0,
    featureflag: ProjectFeatureFlag.OptimizedFunnelSelection,
  }),
  [FeatureRolloutName.TextAnalysis]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.TextAnalysis,
    percentage: 0,
    featureflag: ProjectFeatureFlag.TextAnalysis,
  }),
  [FeatureRolloutName.IssuesHub]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.IssuesHub,
    percentage: 0,
    featureflag: ProjectFeatureFlag.LexiconIssuesHub,
  }),
  [FeatureRolloutName.IssuesHubSidePanel]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.IssuesHubSidePanel,
    percentage: 100,
    featureflag: ProjectFeatureFlag.LexiconIssuesHubSidePanel,
  }),
  [FeatureRolloutName.LexiconCohortCountRefresh]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.LexiconCohortCountRefresh,
    percentage: 100,
    featureflag: ProjectFeatureFlag.LexiconCohortCountRefresh,
  }),
  [FeatureRolloutName.ViewNEverywhere]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.ViewNEverywhere,
    percentage: 100,
    featureflag: ProjectFeatureFlag.ViewNEverywhere,
  }),
  [FeatureRolloutName.MetricTreeAI]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.MetricTreeAI,
    percentage: 0,
    featureflag: ProjectFeatureFlag.MetricTreeAI,
  }),
};
export const RolloutsByFeatureFlag: Partial<Record<ProjectFeatureFlag, FeatureRollout>> = Object.fromEntries(
  Object.values(FeatureRollouts).map((value) => [value.featureflag, value]),
);

export function useTemplateSearch() {
  if (isTestEnv()) {
    return true;
  }
  return isUrlDefinedExperimentalFeatureEnabled(getLocation(), `template_search`, () => isCurrentProjectInternal());
}

export function isSRMultiTimelineEnabled(currentProject?: CurrentProject) {
  const variant = getFeatureFlagVariant(FeatureFlag.SessionRecordingMultiTimeline);
  const hasOverride = currentProject?.hasFeatureFlag(ProjectFeatureFlag.SessionRecordingMultiTimeline);
  return hasOverride || (!!variant && variant === FEATURE_FLAG_VARIANTS.Enabled);
}

export function isSRConsoleEnabled(currentProject?: CurrentProject) {
  const variant = getFeatureFlagVariant(FeatureFlag.SessionRecordingConsole);
  const hasOverride = currentProject?.hasFeatureFlag(ProjectFeatureFlag.SessionRecordingConsole);
  return hasOverride || (!!variant && variant === FEATURE_FLAG_VARIANTS.Treatment);
}

export function isDataVolumeMonitoringEnabled(currentProject?: CurrentProject) {
  if (!currentProject) {
    return false;
  }
  return currentProject.getFeatureGate(FeatureGate.DataGovernance);
}

export function isHeatMapsScrollMapsEnabled(currentProject?: CurrentProject) {
  if (!currentProject) {
    return false;
  }

  const hasSessionReplayEnabled = isSessionRecordingEnabled(currentProject);

  return hasSessionReplayEnabled && FeatureRollouts[FeatureRolloutName.ScrollMaps].isEnabled(currentProject);
}

export function getSessionRecordingBlockingInfo(currentProject?: CurrentProject) {
  if (!currentProject) {
    return [];
  }
  return currentProject.blocking.blockedReplayWindows || [];
}

export function isSessionRecordingEnabled(currentProject?: CurrentProject) {
  if (!currentProject) {
    return false;
  }

  const hasSessionReplayFeatureGate = currentProject.getFeatureGate(FeatureGate.SessionReplay);
  const hasFeatureFlagEnabled = currentProject.hasFeatureFlag(ProjectFeatureFlag.SessionRecording);
  const hasSessionReplayEnabled = hasSessionReplayFeatureGate || hasFeatureFlagEnabled;
  if (currentProject.hasFeatureFlag(ProjectFeatureFlag.SessionRecordingSensitiveData)) {
    return hasSessionReplayEnabled && currentProject.hasSensitiveAccess;
  }

  return hasSessionReplayEnabled;
}

export const areSessionRecordingPlaylistsEnabled = (currentProject?: CurrentProject) =>
  isFeatureFlagVariantEnabled(
    currentProject,
    FeatureFlag.SessionRecordingPlaylists,
    ProjectFeatureFlag.SessionRecordingPlaylists,
  );

export const isSessionRecordingRemoteSettingEnabled = (currentProject?: CurrentProject) =>
  isFeatureFlagVariantEnabled(
    currentProject,
    FeatureFlag.SessionRecordingRemoteSetting,
    ProjectFeatureFlag.SessionRecordingRemoteSetting,
  );

export function isSegmentRegionEnabled() {
  return true;
}

export function isDataStandardsEnabled(currentProject?: CurrentProject | ProjectDetails) {
  return isDevEnv() || currentProject?.getFeatureGate(FeatureGate.DataStandards);
}

export function isAutomateEventApprovalEnabled(currentProject?: CurrentProject | ProjectDetails) {
  return currentProject?.getFeatureGate(FeatureGate.DataStandards);
}

export function isDisableVisibleOnNonCompliantEnabled(currentProject?: CurrentProject | ProjectDetails): boolean {
  if (isDevEnv()) {
    return true;
  }

  if (!currentProject) {
    return false;
  }

  return (
    currentProject.getFeatureGate(FeatureGate.DataStandards) && // has data standards feature gate
    isDataStandardsEnabled(currentProject) && // data standards feature is enabled
    currentProject.getFeatureGate(FeatureGate.DataGovernance) && // has event approval feature gate, this check is necessary in case account downgrade
    currentProject?.hasEventApprovalEnabled && // event approval is enabled and turned on
    FeatureRollouts[FeatureRolloutName.DisableVizOnNonCompliant].isEnabled(currentProject)
  );
}

export function isCompanyProfileEnabled(currentProject?: CurrentProject | ProjectDetails | null) {
  if (isDevEnv()) {
    return true;
  }
  return FeatureRollouts[FeatureRolloutName.CompanyProfile].isEnabled(currentProject);
}

export function isExcludeDateEnabled(currentProject?: CurrentProject | ProjectDetails | null) {
  // Not using the old feature flag system for this rollout, but this enables feature flag overrides to work
  // which is helpful for testing in dev, QA, and dogfooding
  const legacyCheck = currentProject?.hasFeatureFlag(ProjectFeatureFlag.ExcludeDate);
  // Exclude is bundled to the Unified & Compact Date Picker Rollouts
  return legacyCheck || isUnifiedDatePickerEnabled(currentProject) || isCompactDatePickerEnabled(currentProject);
}

export function isFutureDateSelectionEnabled(currentProject?: CurrentProject | ProjectDetails | null) {
  if (!currentProject) {
    return false;
  }
  return isUnifiedDatePickerEnabled(currentProject) || isCompactDatePickerEnabled(currentProject);
}

export function isUnifiedDatePickerEnabled(currentProject?: CurrentProject | ProjectDetails | null): boolean {
  // Not using the old feature flag system for this rollout, but this enables feature flag overrides to work
  // which is helpful for testing in dev, QA, and dogfooding
  const legacyCheck = currentProject?.hasFeatureFlag(ProjectFeatureFlag.UnifiedDatePicker);
  if (legacyCheck) {
    return true;
  }

  const variant = getFeatureFlagVariant(FeatureFlag.UnifiedDatePicker);
  return !!variant && variant === `unified`;
}

export function isCompactDatePickerEnabled(currentProject?: CurrentProject | ProjectDetails | null): boolean {
  // Not using the old feature flag system for this rollout, but this enables feature flag overrides to work
  // which is helpful for testing in dev, QA, and dogfooding
  const legacyCheck = currentProject?.hasFeatureFlag(ProjectFeatureFlag.CompactDatePicker);
  if (legacyCheck) {
    return true;
  }

  const variant = getFeatureFlagVariant(FeatureFlag.UnifiedDatePicker);
  return !!variant && variant === `compact`;
}

export function isDateLimitsEnabled() {
  const variant = getFeatureFlagVariant(FeatureFlag.InsightsDateLimits);
  return isDevEnv() || (!!variant && variant === FEATURE_FLAG_VARIANTS.Enabled);
}

export function isMetricLevelDataGroupsEnabled(currentProject?: CurrentProject | ProjectDetails | null) {
  if (!currentProject) {
    return false;
  }
  return (
    currentProject.hasFeatureFlag(ProjectFeatureFlag.FirstClassMetrics) ||
    // Also check metric level data groups feature flag if its in a testing environment
    // Till we setup ReportBookmarkManagerProvider for b2b-metrics.stories.tsx
    (currentProject.hasFeatureFlag(ProjectFeatureFlag.MetricLevelDataGroups) && isTestEnv())
  );
}

export function isCanvasEnabled(currentProject: CurrentProject | null) {
  if (!currentProject) {
    return false;
  }
  return currentProject.getFeatureGate(FeatureGate.MetricTree);
}

export function isGoalEnabled(currentProject?: CurrentProject) {
  if (!currentProject) {
    return false;
  }

  return currentProject.hasFeatureFlag(ProjectFeatureFlag.Goal);
}

export function isNullPropertyMonitoringEnabled(currentProject: CurrentProject | null | undefined) {
  return (
    FeatureRollouts[FeatureRolloutName.NullPropertyMonitoring].isEnabled(currentProject) ||
    isDevEnv() ||
    isCurrentProjectInternal()
  );
}

function getFeatureFlagVariant(flagName: FeatureFlag, fallbackValue?: string) {
  if (typeof mixpanel === `undefined`) {
    return fallbackValue;
  }

  return mixpanel.flags?.get_variant_value_sync(flagName, fallbackValue);
}

/**
 * Check if a feature flag is enabled synchronously.
 * Similar to isFeatureFlagEnabled but uses synchronous check without currentProject.
 */
export function isFeatureFlagEnabledSync(flagName: FeatureFlag, fallbackValue: boolean = false): boolean {
  return mixpanel.flags?.is_enabled_sync(flagName, fallbackValue) ?? fallbackValue;
}

// Use when using an experiment feature flag and checking for enablement.
export function isFeatureFlagVariantEnabled(
  currentProject: CurrentProject | null | undefined,
  flagName: FeatureFlag,
  projectFeatureFlagName: ProjectFeatureFlag,
) {
  const variant = getFeatureFlagVariant(flagName);
  const hasOverride = currentProject?.hasFeatureFlag(projectFeatureFlagName);
  return hasOverride || (!!variant && variant === FEATURE_FLAG_VARIANTS.Enabled);
}

// Use when using a feature gate feature flag and checking for enablement.
export function isFeatureFlagEnabled(
  currentProject: CurrentProject | null | undefined,
  flagName: FeatureFlag,
  projectFeatureFlagName: ProjectFeatureFlag,
) {
  return currentProject?.hasFeatureFlag(projectFeatureFlagName) || mixpanel.flags?.is_enabled(flagName, false);
}

export function isIssuesHubEnabled(currentProject?: CurrentProject | ProjectDetails) {
  return (
    FeatureRollouts[FeatureRolloutName.IssuesHub].isEnabled(currentProject ?? null) ||
    isDevEnv() ||
    isCurrentProjectInternal()
  );
}

export function isIssuesHubSidePanelEnabled(currentProject?: CurrentProject | ProjectDetails) {
  return (
    FeatureRollouts[FeatureRolloutName.IssuesHubSidePanel].isEnabled(currentProject ?? null) ||
    currentProject?.hasFeatureFlag(ProjectFeatureFlag.LexiconIssuesHubSidePanel)
  );
}

export function isPropertyTypeMonitoringEnabled(currentProject: CurrentProject | null | undefined) {
  return (
    FeatureRollouts[FeatureRolloutName.PropertyTypeMonitoring].isEnabled(currentProject) ||
    currentProject?.hasFeatureFlag(ProjectFeatureFlag.PropertyTypeMonitoring)
  );
}

export function isAdvancedDeletionEventCountBypassEnabled(currentProject?: CurrentProject) {
  if (!currentProject) {
    return false;
  }

  return currentProject.hasFeatureFlag(ProjectFeatureFlag.AdvDelEventCountBypass);
}

export function isAdvancedDeletionRequestPerMonthBypassEnabled(currentProject?: CurrentProject) {
  if (!currentProject) {
    return false;
  }

  return currentProject.hasFeatureFlag(ProjectFeatureFlag.AdvDelRequestPerMonthBypass);
}

export function isJHSAdvancedDropEnabled(currentProject?: CurrentProject) {
  if (!currentProject) {
    return false;
  }

  return currentProject?.hasFeatureFlag(ProjectFeatureFlag.JHSAdvancedDrop);
}

export function isLexiconCohortCountRefreshEnabled(currentProject?: CurrentProject) {
  if (!currentProject) {
    return false;
  }

  return (
    FeatureRollouts[FeatureRolloutName.LexiconCohortCountRefresh].isEnabled(currentProject ?? null) ||
    currentProject?.hasFeatureFlag(ProjectFeatureFlag.LexiconCohortCountRefresh) ||
    isCurrentProjectInternal()
  );
}

export function isViewNEverywhereEnabled(currentProject?: CurrentProject | ProjectDetails): boolean {
  if (!currentProject) {
    return false;
  }

  return FeatureRollouts[FeatureRolloutName.ViewNEverywhere].isEnabled(currentProject);
}

/** Evaluates the org feature flag, or forces the feature on for internal projects, since we cannot add feature flags to Org 1 as of 2025-11-07 */
export function isExperimentMetricHoverCardsEnabled(currentProject?: CurrentProject | ProjectDetails) {
  return currentProject?.hasFeatureFlag(ProjectFeatureFlag.ExperimentMetricHoverCards) || isCurrentProjectInternal();
}

export function isExperimentsHealthEnabled(currentProject?: CurrentProject | ProjectDetails) {
  return currentProject?.hasFeatureFlag(ProjectFeatureFlag.ExperimentsHealth) || isCurrentProjectInternal();
}

enum ViewNInBarChartVariant {
  Control = `control`,
  Treatment = `treatment`,
}

export function isViewNInBarChartEnabled(currentProject: CurrentProject | undefined): boolean {
  if (isTestEnv()) {
    return false;
  }
  const variant = getFeatureFlagVariant(FeatureFlag.ViewNInBarChart);
  return Boolean(
    currentProject?.hasFeatureFlag(ProjectFeatureFlag.ViewNInBarChart) || variant === ViewNInBarChartVariant.Treatment,
  );
}

export function isAllEventsDisabled(currentProject: CurrentProject | undefined): boolean {
  return Boolean(currentProject?.hasFeatureFlag(ProjectFeatureFlag.DisableAllEvents));
}

export function isAnnotationsTimelineEnabled(currentProject: CurrentProject | undefined): boolean {
  return (
    currentProject?.hasFeatureFlag(ProjectFeatureFlag.AnnotationsTimeline) ||
    mixpanel.flags?.get_variant_value_sync(FeatureFlag.AnnotationsTimeline, false)
  );
}
